/*
 * Eventi view_item_list_tmi, select_item_tmi e view_cart_tmi, inviati con push su
 * window.dataLayer della pagina, letto dal GTM che Stape (app di tracking) carica sulla
 * vetrina. Formato degli item allineato agli eventi standard di Stape. Le liste sono i
 * contenitori marcati da snippets/tracking-list.liquid, gli item gli elementi marcati
 * da snippets/tracking-item.liquid. Vedi docs/requests/2026-10-20-tracciamento-eventi-tmi.md
 */
(() => {
  if (window.trackingEventsLoaded || window.Shopify?.designMode) return;
  window.trackingEventsLoaded = true;

  const LIST_SELECTOR = '[data-tracking-list-id]';
  const ITEM_SELECTOR = '[data-tracking-item]';
  const CLONE_SELECTOR = '.splide__slide--clone';
  const CART_SELECTOR = '#CartDrawer-CartItems [data-tracking-cart-value]';
  const CART_ITEM_SELECTOR = '.cart-item[data-tracking-item]';
  const SELECT_TRIGGER_SELECTOR = 'a[href], quick-view';
  // Link dentro la card che portano a un altro prodotto (sibling di colore) o alla collezione
  const SELECT_IGNORE_SELECTOR = '.card-product-siblings, .color-swatch__view-more';

  const push = (eventName, ecommerce) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: eventName, ecommerce });
    } catch (error) {
      // Il tracciamento non deve mai interrompere la pagina
    }
  };

  const compact = (object) => {
    Object.keys(object).forEach((key) => {
      if (object[key] === '' || object[key] === null || object[key] === undefined) delete object[key];
    });
    return object;
  };

  const parseItem = (element) => {
    try {
      return JSON.parse(element.dataset.trackingItem);
    } catch (error) {
      return null;
    }
  };

  const toAmount = (cents) => Math.round(cents) / 100;

  // index parte da 1 come negli eventi di Stape
  const toItem = (raw, index, extra = {}) =>
    compact({
      ...raw,
      price: toAmount(raw.price),
      compare_at_price: raw.compare_at_price ? String(toAmount(raw.compare_at_price)) : null,
      index: index + 1,
      ...extra,
    });

  const currency = () => window.Shopify?.currency?.active;

  const listInfo = (list) => ({
    item_list_id: list.dataset.trackingListId,
    item_list_name: list.dataset.trackingListName,
  });

  // Card della lista senza i cloni dei caroselli Splide in loop
  const listRawItems = (list) =>
    Array.from(list.querySelectorAll(ITEM_SELECTOR))
      .filter((card) => !card.closest(CLONE_SELECTOR) && card.closest(LIST_SELECTOR) === list)
      .map(parseItem)
      .filter(Boolean);

  const isVisible = (element) =>
    typeof element.checkVisibility === 'function'
      ? element.checkVisibility({ opacityProperty: true, visibilityProperty: true })
      : true;

  /* view_item_list_tmi */

  const sentLists = new Set();
  const pendingLists = new Set();
  let pendingTimer = null;

  const trackListView = (list) => {
    const info = listInfo(list);
    const items = listRawItems(list).map((raw, index) => toItem(raw, index, info));
    if (!items.length) return;
    const key = `${info.item_list_id}|${items.map((item) => item.item_variant).join(',')}`;
    if (sentLists.has(key)) return;
    sentLists.add(key);
    push('view_item_list_tmi', { ...info, currency: currency(), items });
  };

  // Liste nel viewport ma nascoste con opacity/visibility (es. promotion popup chiuso)
  const checkPendingLists = () => {
    pendingLists.forEach((list) => {
      if (!list.isConnected) {
        pendingLists.delete(list);
      } else if (isVisible(list)) {
        trackListView(list);
        pendingLists.delete(list);
        listObserver.unobserve(list);
      }
    });
    if (!pendingLists.size) {
      clearInterval(pendingTimer);
      pendingTimer = null;
    }
  };

  const listObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const list = entry.target;
        if (!entry.isIntersecting) {
          pendingLists.delete(list);
          return;
        }
        if (isVisible(list)) {
          trackListView(list);
          listObserver.unobserve(list);
          return;
        }
        pendingLists.add(list);
        if (!pendingTimer) pendingTimer = setInterval(checkPendingLists, 1000);
      });
    },
    { rootMargin: '0px 0px -20% 0px' }
  );

  const observeLists = (root) => {
    if (root.matches(LIST_SELECTOR)) listObserver.observe(root);
    root.querySelectorAll(LIST_SELECTOR).forEach((list) => listObserver.observe(list));
  };

  // Liste caricate dopo il rendering: correlati, visti di recente, complementari, quick view
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) observeLists(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  observeLists(document.body);

  /* select_item_tmi */

  const trackSelect = (trigger) => {
    if (!trigger || trigger.closest(SELECT_IGNORE_SELECTOR)) return;
    const card = trigger.closest(ITEM_SELECTOR);
    const list = card?.closest(LIST_SELECTOR);
    if (!list) return;
    const raw = parseItem(card);
    if (!raw) return;
    const info = listInfo(list);
    const index = listRawItems(list).findIndex((item) => item.item_variant === raw.item_variant);
    push('select_item_tmi', {
      ...info,
      currency: currency(),
      items: [toItem(raw, Math.max(index, 0), info)],
    });
  };

  document.addEventListener('click', (event) => trackSelect(event.target.closest(SELECT_TRIGGER_SELECTOR)), true);

  // <quick-view> si apre anche con Invio/Spazio da keydown (preventDefault), senza click
  document.addEventListener(
    'keydown',
    (event) => {
      if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;
      trackSelect(event.target.closest('quick-view'));
    },
    true
  );

  /* view_cart_tmi */

  // Il drawer viene ridisegnato dal Liquid prima di ogni open(): le righe sono aggiornate
  const trackCartView = () => {
    const cart = document.querySelector(CART_SELECTOR);
    if (!cart) return;
    const items = Array.from(cart.querySelectorAll(CART_ITEM_SELECTOR))
      .map(parseItem)
      .filter(Boolean)
      .map((raw, index) => toItem(raw, index));
    if (!items.length) return;
    push('view_cart_tmi', {
      currency: cart.dataset.trackingCartCurrency,
      value: String(toAmount(Number(cart.dataset.trackingCartValue))),
      items,
    });
  };

  // CartDrawer.open() aggiunge la classe active, sia dall'icona sia dopo l'aggiunta al carrello
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    let drawerActive = cartDrawer.classList.contains('active');
    new MutationObserver(() => {
      const active = cartDrawer.classList.contains('active');
      if (active && !drawerActive) trackCartView();
      drawerActive = active;
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
})();
